import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import axiosInstance from '../../api';
import './UserActivityPage.css';

const PAGE_SIZE = 50;

function badge(success) {
  return success
    ? <span className="ua-badge ua-badge-ok">{i18n.t('UserActivityPage.statusOk')}</span>
    : <span className="ua-badge ua-badge-fail">{i18n.t('UserActivityPage.statusFail')}</span>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function actionLabel(action) {
  if (!action) return '—';
  const [prefix, ...rest] = action.split(':');
  return (
    <span className="ua-action">
      <span className="ua-action-prefix">{prefix}</span>
      {rest.length > 0 && <span className="ua-action-path">:{rest.join(':')}</span>}
    </span>
  );
}

export default function UserActivityPage() {
  const { t } = useTranslation();

  // Org + user selection
  const [orgs, setOrgs]           = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers]   = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orgsLoading, setOrgsLoading]   = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);

  // Activity log
  const [logs, setLogs]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [filter, setFilter] = useState('');
  const [hasQueried, setHasQueried] = useState(false);

  // Load all orgs on mount
  useEffect(() => {
    axiosInstance.get('/organizations')
      .then(r => setOrgs(r.data || []))
      .catch(() => setOrgs([]))
      .finally(() => setOrgsLoading(false));
  }, []);

  // Load users when org changes
  useEffect(() => {
    if (!selectedOrg) { setOrgUsers([]); setSelectedUser(null); return; }
    setUsersLoading(true);
    setSelectedUser(null);
    setLogs([]);
    setHasQueried(false);
    axiosInstance.get(`/admin/orgs/${selectedOrg.id}/users`)
      .then(r => setOrgUsers(r.data || []))
      .catch(() => setOrgUsers([]))
      .finally(() => setUsersLoading(false));
  }, [selectedOrg]);

  const loadLogs = useCallback(async (email, targetPage) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(
        `/admin/activity-log/user/${encodeURIComponent(email)}`,
        { params: { page: targetPage, size: PAGE_SIZE } }
      );
      setLogs(res.data.content || []);
      setTotal(res.data.totalElements || 0);
      setHasQueried(true);
    } catch {
      setError(t('UserActivityPage.errorLoadLogs'));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Auto-load when user + page changes
  useEffect(() => {
    if (selectedUser) loadLogs(selectedUser.email, page);
  }, [selectedUser, page, loadLogs]);

  const handleOrgChange = (e) => {
    const org = orgs.find(o => String(o.id) === e.target.value) || null;
    setSelectedOrg(org);
    setLogs([]);
    setTotal(0);
    setPage(0);
    setFilter('');
    setHasQueried(false);
  };

  const handleUserChange = (e) => {
    const user = orgUsers.find(u => String(u.id) === e.target.value) || null;
    setSelectedUser(user);
    setPage(0);
    setFilter('');
    setLogs([]);
    setHasQueried(false);
  };

  const exportCSV = () => {
    const rows = [
      [
        t('UserActivityPage.csvTimestamp'), t('UserActivityPage.csvAction'),
        t('UserActivityPage.csvEndpoint'), t('UserActivityPage.csvMethod'),
        t('UserActivityPage.csvIp'), t('UserActivityPage.csvCity'),
        t('UserActivityPage.csvCountry'), t('UserActivityPage.csvIsp'),
        t('UserActivityPage.csvUserAgent'), t('UserActivityPage.csvSuccess'),
      ],
      ...logs.map(l => [
        fmtDate(l.createdAt), l.action, l.endpoint, l.httpMethod,
        l.ipAddress, l.city, l.country, l.isp,
        `"${(l.userAgent || '').replace(/"/g, "'")}"`,
        l.success ? t('UserActivityPage.csvYes') : t('UserActivityPage.csvNo'),
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `activity_${selectedUser?.email}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayed = filter
    ? logs.filter(l =>
        (l.action   || '').toLowerCase().includes(filter.toLowerCase()) ||
        (l.endpoint || '').toLowerCase().includes(filter.toLowerCase()) ||
        (l.ipAddress || '').includes(filter)
      )
    : logs;

  const logins       = logs.filter(l => l.action === 'LOGIN').length;
  const failedLogins = logs.filter(l => l.action === 'LOGIN_FAILED').length;
  const apiCalls     = logs.filter(l => l.action?.startsWith('API_')).length;
  const uniqueIPs    = new Set(logs.map(l => l.ipAddress).filter(Boolean)).size;

  return (
    <div className="ua-page">

      {/* ── Selector bar ── */}
      <div className="ua-selector-bar">
        <div className="ua-selector-group">
          <label className="ua-selector-label">
            <i className="fas fa-building" /> {t('UserActivityPage.organizationLabel')}
          </label>
          <select
            className="ua-selector-select"
            value={selectedOrg?.id ?? ''}
            onChange={handleOrgChange}
            disabled={orgsLoading}
          >
            <option value="">{orgsLoading ? t('UserActivityPage.loading') : t('UserActivityPage.selectOrganization')}</option>
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.organizationName}</option>
            ))}
          </select>
        </div>

        <i className="fas fa-chevron-right ua-selector-arrow" />

        <div className="ua-selector-group">
          <label className="ua-selector-label">
            <i className="fas fa-user" /> {t('UserActivityPage.userLabel')}
          </label>
          <select
            className="ua-selector-select"
            value={selectedUser?.id ?? ''}
            onChange={handleUserChange}
            disabled={!selectedOrg || usersLoading}
          >
            <option value="">
              {!selectedOrg ? t('UserActivityPage.selectOrgFirst') : usersLoading ? t('UserActivityPage.loading') : orgUsers.length === 0 ? t('UserActivityPage.noUsersFound') : t('UserActivityPage.selectUser')}
            </option>
            {orgUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.firstName || u.lastName
                  ? `${u.firstName} ${u.lastName}`.trim() + ` (${u.email})`
                  : u.email || u.userName}
              </option>
            ))}
          </select>
        </div>

        <button
          className="ua-export-btn"
          onClick={exportCSV}
          disabled={logs.length === 0}
          style={{ marginLeft: 'auto' }}
        >
          <i className="fas fa-download" /> {t('UserActivityPage.exportCsv')}
        </button>
      </div>

      {/* ── Stat bar — only shown after a query ── */}
      {hasQueried && (
        <div className="ua-stat-bar">
          <div className="ua-stat">
            <span className="ua-stat-val">{total}</span>
            <span className="ua-stat-label">{t('UserActivityPage.statTotalEvents')}</span>
          </div>
          <div className="ua-stat">
            <span className="ua-stat-val">{logins}</span>
            <span className="ua-stat-label">{t('UserActivityPage.statLogins')}</span>
          </div>
          <div className="ua-stat ua-stat-warn">
            <span className="ua-stat-val">{failedLogins}</span>
            <span className="ua-stat-label">{t('UserActivityPage.statFailedLogins')}</span>
          </div>
          <div className="ua-stat">
            <span className="ua-stat-val">{apiCalls}</span>
            <span className="ua-stat-label">{t('UserActivityPage.statApiCalls')}</span>
          </div>
          <div className="ua-stat">
            <span className="ua-stat-val">{uniqueIPs}</span>
            <span className="ua-stat-label">{t('UserActivityPage.statUniqueIps')}</span>
          </div>
        </div>
      )}

      {/* ── Placeholder when nothing selected ── */}
      {!selectedUser && !loading && (
        <div className="ua-empty-state">
          <i className="fas fa-user-clock ua-empty-icon" />
          <p>{t('UserActivityPage.emptyStatePrompt')}</p>
        </div>
      )}

      {/* ── Table card ── */}
      {selectedUser && (
        <div className="ua-card">
          <div className="ua-toolbar">
            <span className="ua-table-label">
              <i className="fas fa-user-clock" />
              {' '}{selectedUser.firstName || selectedUser.lastName
                ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim()
                : selectedUser.userName}
              {' '}
              <span className="ua-table-email">({selectedUser.email})</span>
              {selectedUser.orgRole && (
                <span className="ua-role-pill">{selectedUser.orgRole}</span>
              )}
            </span>
            <div className="ua-filter-wrap">
              <i className="fas fa-search ua-filter-icon" />
              <input
                className="ua-filter-input"
                placeholder={t('UserActivityPage.filterPlaceholder')}
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
              {filter && (
                <button className="ua-filter-clear" onClick={() => setFilter('')}>
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </div>

          {loading && <div className="ua-spinner-wrap"><div className="ua-spinner" /></div>}
          {error   && <div className="ua-error">{error}</div>}

          {!loading && !error && (
            <div className="ua-table-wrap">
              <table className="ua-table">
                <thead>
                  <tr>
                    <th>{t('UserActivityPage.colTimestamp')}</th>
                    <th>{t('UserActivityPage.colAction')}</th>
                    <th>{t('UserActivityPage.colIpAddress')}</th>
                    <th>{t('UserActivityPage.colLocation')}</th>
                    <th>{t('UserActivityPage.colIsp')}</th>
                    <th>{t('UserActivityPage.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.length === 0 ? (
                    <tr><td colSpan={6} className="ua-empty">{t('UserActivityPage.noActivityFound')}</td></tr>
                  ) : (
                    displayed.map(l => (
                      <tr key={l.id} className={l.success ? '' : 'ua-row-fail'}>
                        <td className="ua-td-ts">{fmtDate(l.createdAt)}</td>
                        <td>{actionLabel(l.action)}</td>
                        <td className="ua-td-ip">{l.ipAddress || <span className="ua-dim">—</span>}</td>
                        <td className="ua-td-loc">
                          {l.city || l.country
                            ? `${l.city || ''}${l.city && l.country ? ', ' : ''}${l.country || ''}`
                            : <span className="ua-dim">—</span>}
                        </td>
                        <td className="ua-td-isp">{l.isp || <span className="ua-dim">—</span>}</td>
                        <td>{badge(l.success)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="ua-pagination">
              <button className="ua-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <i className="fas fa-chevron-left" /> {t('UserActivityPage.prev')}
              </button>
              <span className="ua-page-info">{t('UserActivityPage.pageInfo', { current: page + 1, total: Math.ceil(total / PAGE_SIZE) })}</span>
              <button className="ua-page-btn" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>
                {t('UserActivityPage.next')} <i className="fas fa-chevron-right" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Recent user-agents ── */}
      {displayed.some(l => l.userAgent) && (
        <div className="ua-card ua-ua-card">
          <div className="ua-section-label">{t('UserActivityPage.recentUserAgents')}</div>
          <div className="ua-ua-list">
            {[...new Set(displayed.map(l => l.userAgent).filter(Boolean))].slice(0, 5).map((ua, i) => (
              <div key={i} className="ua-ua-item">{ua}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
